import { useContext, useState, useEffect } from 'react';
import { Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import axios from 'axios';
import AppContext from '../../../context/AppContext';
import '../../../assets/css/Admin/Notifications/Notifications.css';

const Notifications = () => {
  const { baseUrl, authToken } = useContext(AppContext);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedNotification, setSelectedNotification] = useState(null);

  const getTypeColor = (category) => {
    switch (category) {
      case 'ride': return '#1B9B82';
      case 'driver': return '#4A90E2';
      case 'customer': return '#4A90E2';
      case 'wallet': return '#F5A623';
      case 'kyc': return '#4A90E2';
      case 'ticket': return '#3b82f6';
      case 'offer': return '#8b5cf6';
      default: return '#1B9B82';
    }
  };

  const getTypeIcon = (category) => {
    switch (category) {
      case 'ride': return '🚗';
      case 'driver': return '✅';
      case 'customer': return '👤';
      case 'wallet': return '💰';
      case 'kyc': return '📄';
      case 'ticket': return '🎫';
      case 'offer': return '🏷️';
      default: return '🔔';
    }
  };

  const viewAllNotification = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/Notification/viewAllNotification`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          setNotifications(response.data.notifications);
        } else if (response.data.success === false) {
          setErrorMessage(response.data.message);
        }
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage("Unable to load notifications right now.");
      })
      .finally(() => setLoading(false));
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);

    if (!notification.is_read) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, is_read: 1 } : n))
      );
      axios
        .post(
          `${baseUrl}/admin/Notification/markAsRead/${notification.id}`,
          {},
          { headers: { Authorization: `Bearer ${authToken}` } }
        )
        .catch((error) => console.log(error));
    }
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    axios
      .post(
        `${baseUrl}/admin/Notification/markAllAsRead`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .catch((error) => console.log(error));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    viewAllNotification();
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="notifications-page">
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">Notifications</h2>
                <p className="text-muted mb-0">
                  You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                </p>
              </div>
              {notifications.length > 0 && (
                <Button
                  onClick={markAllAsRead}
                  style={{ backgroundColor: '#1B9B82', border: 'none' }}
                  className="px-4"
                >
                  Mark All as Read
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#1B9B82' }} />
          </div>
        )}

        {!loading && errorMessage && (
          <p className="text-center text-muted mt-4">{errorMessage}</p>
        )}

        {!loading && !errorMessage && notifications.length === 0 && (
          <p className="text-center text-muted mt-4">No notifications yet</p>
        )}

        {!loading && !errorMessage && notifications.length > 0 && (
          <Row>
            <Col lg={selectedNotification ? 7 : 12}>
              <Card className="notifications-card">
                <Card.Body className="p-0">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`notification-item ${!notification.is_read ? 'unread' : ''} ${
                        selectedNotification?.id === notification.id ? 'selected' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-icon" style={{ backgroundColor: getTypeColor(notification.category) }}>
                        {getTypeIcon(notification.category)}
                      </div>
                      <div className="notification-content">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <h6 className="mb-0">{notification.title}</h6>
                          <small className="text-muted">{notification.time_ago || notification.created_at}</small>
                        </div>
                        <p className="notification-description mb-0">{notification.description}</p>
                      </div>
                      {!notification.is_read && <div className="unread-indicator"></div>}
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            {selectedNotification && (
              <Col lg={5}>
                <Card className="notification-details-card">
                  <Card.Body>
                    <div className="text-end mb-3">
                      <Button
                        variant="link"
                        className="text-muted p-0"
                        onClick={() => setSelectedNotification(null)}
                      >
                        ✕ Close
                      </Button>
                    </div>

                    <div className="text-center mb-4">
                      <div
                        className="notification-large-icon mx-auto mb-3"
                        style={{ backgroundColor: getTypeColor(selectedNotification.category) }}
                      >
                        {getTypeIcon(selectedNotification.category)}
                      </div>
                      <h5>{selectedNotification.title}</h5>
                      <p className="text-muted">{selectedNotification.time_ago || selectedNotification.created_at}</p>
                    </div>

                    <div className="notification-details">
                      <h6 className="mb-3">Details</h6>
                      <p className="mb-4">{selectedNotification.description}</p>

                      <div className="detail-row">
                        <span className="detail-label">Category:</span>
                        <span className="detail-value">{selectedNotification.category}</span>
                      </div>
                      {selectedNotification.subtitle && (
                        <div className="detail-row">
                          <span className="detail-label">Subtitle:</span>
                          <span className="detail-value">{selectedNotification.subtitle}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4">
                      <Button
                        variant="outline-secondary"
                        className="w-100"
                        onClick={() => setSelectedNotification(null)}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        )}
      </Container>
    </div>
  );
};

export default Notifications;